import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import Spinner from '~/common/components/utils/Spinner';
import useAuth from '~/features/auth/hooks/useAuth.ts';
import { buildSignInPathWithReturnTo } from '~/features/auth/utils/auth.util.ts';

import type { PropsWithChildren } from 'react';

/**
 * 보호 라우트 인증 게이트.
 *
 * @description 진입 시점의 축출은 라우트 `loader`(`~/routes`)가 맡는다 — 렌더 결과(`<Navigate>`)로
 *  하는 축출은 POP(뒤로가기) 전환에서 삼켜져 빈 화면 고착을 만들기 때문이다.
 *  이 컴포넌트는 그 이후를 담당한다: ① 세션 확인이 끝나지 않았으면 대기(스피너),
 *  ② 확인 결과 로그인 상태가 아니면 현재 경로를 `returnTo` 에 실어 로그인으로 축출.
 *
 *  ②가 필요한 이유는 loader 가 판정한 뒤에도 세션이 사라질 수 있기 때문이다(만료·다른 탭 로그아웃).
 *  그 경우에도 로그인 후 보던 화면으로 되돌아와야 한다.
 */
function AuthGuard({ children }: PropsWithChildren) {
  const {
    state: { authUser, isLoading },
    fetchAuthUser,
  } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // 세션 복원은 한 번만 — 이미 확인이 끝났으면 다시 조회하지 않는다.
    if (!authUser && isLoading) fetchAuthUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, isLoading]);

  // 확인 전에는 판단을 미룬다. 여기서 곧바로 축출하면 새로고침마다 로그인 화면이 번쩍인다.
  if (isLoading) {
    return <Spinner fullScreen />;
  }

  if (!authUser) {
    return (
      <Navigate to={buildSignInPathWithReturnTo(location.pathname, location.search)} replace />
    );
  }

  return children;
}

export default AuthGuard;
